import { format } from 'date-fns';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { chatMessagesAtom } from '~/atoms/dashboardAtom';
import { useDashboard, useUpdateDashboard } from '~/hooks/useDashboard';
import { Citation, ParsedStreamChatMessage, WidgetAction } from '../types';
import { applyHoldingMessage } from '../utils/applyHoldingMessage';
import { cleanupEventSources } from '../utils/cleanupEventSources';
import { getAuthToken } from '../utils/getAuthToken';
import { parseRawWidgetIds } from '../utils/parseRawWidgetIds';
import { subscribeToChat } from '../utils/subscribeToChat';
import { subscribeToUpdates } from '../utils/subscribeToUpdates';

export const agentUrl = process.env.REACT_AGENT_URL;

export const getMessageId = (str: 'user' | 'agent') => {
  return `${str}-${Date.now().toString()}`;
};

export type HoldingMessage = {
  type: 'holding';
  id: string;
  content: string;
};

export const chatPayloadSchema = z.object({
  prompt: z.string(),
  stream: z.boolean(),
  state: z.object({
    selected_dashboard_id: z.string(),
    selected_widget_ids: z.array(z.string()).optional(),
    selected_portfolio_id: z.string().optional(),
  }),
});

export interface UseStreamChatOptions {
  dashboardId?: string;
  portfolioId?: string;
  maxRetries?: number;
  retryDelay?: number;
}

export const useStreamChat = ({
  dashboardId,
  portfolioId,
  maxRetries = 3,
  retryDelay = 1000,
}: UseStreamChatOptions) => {
  const updateDashboard = useUpdateDashboard(dashboardId ?? '');

  // Access current dashboard config for widget removals
  const { data: dashboardData } = useDashboard(dashboardId);

  // Global atom for persistence across popouts
  const [allChatMessages, setAllChatMessages] = useAtom(chatMessagesAtom);

  // Local state for messages - initialize from atom if available
  const [isInitialized, setIsInitialized] = useState(false);
  const [parsedMessages, setParsedMessages] = useState<
    ParsedStreamChatMessage[]
  >(() => {
    // Initialize with atom data if it exists
    if (dashboardId && allChatMessages[dashboardId]) {
      return allChatMessages[dashboardId];
    }
    return [];
  });
  useEffect(() => {
    if (!isInitialized && parsedMessages.length > 0) {
      setIsInitialized(true);
    }
  }, [parsedMessages, isInitialized]);

  // Sync local messages to atom whenever they change
  useEffect(() => {
    if (dashboardId && parsedMessages.length > 0) {
      setAllChatMessages((prev) => ({
        ...prev,
        [dashboardId]: parsedMessages,
      }));
    }
  }, [parsedMessages, dashboardId, setAllChatMessages]);

  const [isProcessing, setIsProcessing] = useState(false);

  const addUserMessage = useCallback((userQuery: string) => {
    const userMessageId = getMessageId('user');

    return {
      id: userMessageId,
      content: userQuery,
      timestamp: format(new Date(), 'HH:mm'),
      isUser: true,
    };
  }, []);

  const addInitialAgentMessage = useCallback(() => {
    const messageId = getMessageId('agent');

    return {
      id: messageId,
      content: '',
      timestamp: format(new Date(), 'HH:mm'),
      isUser: false,
      isStreaming: true,
    };
  }, []);

  const streamChatResponse = useCallback(
    async (
      userQuery: string,
      selectedWidgetIds: string[],
      reasoningContext?: { ticker: string; reasoning: string } | null,
    ) => {
      if (!userQuery.trim() || isProcessing) return;

      const userMessage = addUserMessage(userQuery);
      const botMessage = addInitialAgentMessage();

      setParsedMessages((prev) => [...prev, userMessage, botMessage]);
      setIsProcessing(true);

      let latestContent = '';
      let latestCitations: Citation[] | undefined;
      let action: WidgetAction | undefined | null;
      let updatesSubscription: AbortController | null = null;
      let chatSubscription: AbortController | null = null;

      try {
        const token = await getAuthToken();

        if (!token) {
          throw new Error('Authentication token is required');
        }

        if (!dashboardId) {
          throw new Error(
            'Dashboard ID is required. Please navigate to a dashboard to use the chat.',
          );
        }

        updatesSubscription = subscribeToUpdates({
          token,
          maxRetries,
          retryDelay,
          onMessage: (message) => {
            setParsedMessages((prev) => applyHoldingMessage(prev, message));
          },
        });

        // Include reasoning context in the prompt sent to API, but don't show it in user's message
        const promptForAPI = reasoningContext
          ? `${reasoningContext.reasoning}\n\nUser question: ${userQuery}`
          : userQuery;

        const payload = chatPayloadSchema.parse({
          prompt: promptForAPI,
          stream: true,
          state: {
            selected_dashboard_id: dashboardId,
            selected_widget_ids: selectedWidgetIds,
            selected_portfolio_id: portfolioId,
          },
        });

        let isFirstChunk = true;

        const handleWidgetAction = () => {
          setParsedMessages((prev) => {
            const updated = prev.map((msg) =>
              msg.id === botMessage.id ? { ...msg, isStreaming: false } : msg,
            );

            const lastMessage = updated[updated.length - 1];

            if (
              !lastMessage.isUser &&
              lastMessage?.widgetAction &&
              lastMessage?.widgets &&
              dashboardId
            ) {
              if (
                lastMessage.widgetAction === 'add' &&
                lastMessage.widgets.length > 0
              ) {
                lastMessage.widgets.forEach((widget) => {
                  if (widget.name !== 'dynamic') {
                    updateDashboard.mutate({
                      payload: {
                        config: [
                          {
                            id: widget.name,
                          },
                        ],
                      },
                    });
                    return;
                  }

                  updateDashboard.mutate({
                    payload: {
                      config: [
                        {
                          id: crypto.randomUUID(),
                          isDynamic: true,
                          renderComponent: widget.metadata?.renderComponent,
                          query: widget.metadata?.query,
                          shortDescription: widget.metadata?.shortDescription,
                          isAggregate: widget.metadata?.isAggregate,
                        },
                      ],
                    },
                  });
                });
              }

              if (
                lastMessage.widgetAction === 'update' &&
                lastMessage.widgetIdToUpdate &&
                lastMessage.widgetIdToUpdate.length > 0 &&
                lastMessage.widgets.length > 0
              ) {
                const idsToUpdate = lastMessage.widgetIdToUpdate ?? [];

                lastMessage.widgets
                  .filter((w) => w.name === 'dynamic')
                  .forEach((widget) => {
                    idsToUpdate.forEach((updateId) => {
                      updateDashboard.mutate({
                        payload: {
                          config: [
                            {
                              id: updateId,
                              isDynamic: true,
                              renderComponent: widget.metadata?.renderComponent,
                              query: widget.metadata?.query,
                              shortDescription:
                                widget.metadata?.shortDescription,
                              isAggregate: widget.metadata?.isAggregate,
                            },
                          ],
                        },
                      });
                    });
                  });
              }
              if (lastMessage.widgetAction === 'remove' && dashboardId) {
                const prevConfig = dashboardData?.config ?? [];
                const idsToDelete =
                  lastMessage.widgetIdToUpdate &&
                  lastMessage.widgetIdToUpdate.length > 0
                    ? lastMessage.widgetIdToUpdate
                    : (lastMessage.widgets?.map((w) => w.id) ??
                      prevConfig.map((c) => c.id));

                const newConfig = prevConfig.filter(
                  (c) => !idsToDelete.includes(c.id),
                );
                updateDashboard.mutate({
                  payload: { config: newConfig },
                  replace: true,
                });
              }
            }

            return updated;
          });
        };

        chatSubscription = subscribeToChat({
          token,
          payload,
          maxRetries,
          retryDelay,
          onMessage: (chunk) => {
            if (isFirstChunk) {
              isFirstChunk = false;
              if (chunk.content === userQuery) return;
            }

            action = chunk.widget_action;
            latestContent = chunk.content;
            latestCitations = chunk.citations;

            const parsedWidgetIds = parseRawWidgetIds(
              chunk.widget_ids,
              chunk.is_aggregate,
            );

            setParsedMessages((prev) =>
              prev.map((msg) =>
                msg.id === botMessage.id
                  ? {
                      ...msg,
                      content: latestContent,
                      citations: latestCitations,
                      widgetAction: action,
                      widgets: parsedWidgetIds,
                      widgetIdToUpdate: chunk.widget_id_to_update,
                    }
                  : msg,
              ),
            );
          },
          onError: (err) => {
            console.error('Error in streamChatResponse:', err);
            setParsedMessages((prev) =>
              prev.map((msg) =>
                msg.id === botMessage.id
                  ? {
                      ...msg,
                      content:
                        'Sorry, an error occurred while processing your request. Please try again.',
                      isStreaming: false,
                    }
                  : msg,
              ),
            );
            handleWidgetAction();
            cleanupEventSources([updatesSubscription, chatSubscription]);
            setIsProcessing(false);
          },
          onComplete: () => {
            handleWidgetAction();
            setIsProcessing(false);
            cleanupEventSources([updatesSubscription, chatSubscription]);
          },
        });
      } catch (error) {
        console.error('Error in streamChatResponse setup:', error);
        setParsedMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessage.id
              ? {
                  ...msg,
                  content:
                    'Sorry, an error occurred while processing your request. Please try again.',
                  isStreaming: false,
                }
              : msg,
          ),
        );
        setIsProcessing(false);
        throw error;
      }
    },
    [
      isProcessing,
      addUserMessage,
      addInitialAgentMessage,
      maxRetries,
      retryDelay,
      dashboardId,
      portfolioId,
      updateDashboard,
      dashboardData?.config,
    ],
  );

  const initializeChat = useCallback((initialMessage: string) => {
    setParsedMessages([
      {
        id: '1',
        content: initialMessage,
        timestamp: format(new Date(), 'HH:mm'),
        isUser: false,
      },
    ]);
    setIsInitialized(true);
  }, []);

  const appendAgentMessage = useCallback((content: string) => {
    if (!content) return;

    const messageId = getMessageId('agent');
    setParsedMessages((prev) => [
      ...prev,
      {
        id: messageId,
        content,
        timestamp: format(new Date(), 'HH:mm'),
        isUser: false,
        isStreaming: false,
      },
    ]);
  }, []);

  const clearMessages = useCallback(
    (initialMessage?: string) => {
      if (initialMessage) {
        setParsedMessages([
          {
            id: '1',
            content: initialMessage,
            timestamp: format(new Date(), 'HH:mm'),
            isUser: false,
          },
        ]);
        setIsInitialized(true);
      } else {
        setParsedMessages([]);
        setIsInitialized(false);
      }
    },
    [setParsedMessages],
  );

  return {
    messages: parsedMessages,
    isProcessing,
    streamChatResponse,
    initializeChat,
    isInitialized,
    appendAgentMessage,
    clearMessages,
  };
};
